import React, { useState, useEffect, useRef } from 'react';
import {
  createSpreadsheet, writeSheet,
  formatStudentsSheet, formatLessonsSheet,
  formatPaymentsSheet,
} from '../utils/sheets';


function getRangeStart(rangeType, customStart) {
  const now = new Date();
  if (rangeType === 'this_month') return new Date(now.getFullYear(), now.getMonth(), 1);
  if (rangeType === 'last_month') return new Date(now.getFullYear(), now.getMonth() - 1, 1);
  if (rangeType === 'this_quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return new Date(now.getFullYear(), q * 3, 1);
  }
  if (rangeType === 'this_year') return new Date(now.getFullYear(), 0, 1);
  if (rangeType === 'custom') return new Date(customStart);
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

function getRangeEnd(rangeType, customEnd) {
  const now = new Date();
  if (rangeType === 'this_month') return new Date(now.getFullYear(), now.getMonth() + 1, 0);
  if (rangeType === 'last_month') return new Date(now.getFullYear(), now.getMonth(), 0);
  if (rangeType === 'this_quarter') {
    const q = Math.floor(now.getMonth() / 3);
    return new Date(now.getFullYear(), q * 3 + 3, 0);
  }
  if (rangeType === 'this_year') return new Date(now.getFullYear(), 11, 31);
  if (rangeType === 'custom') return new Date(customEnd);
  return now;
}

const EXPORT_OPTIONS = [
  { id: 'students', label: '學員資料總覽',  sub: '姓名、教練、堂數、單堂均價', required: true },
  { id: 'lessons',  label: '上課紀錄明細',  sub: '每堂上課日期、時間、授課教練' },
  { id: 'payments', label: '付款紀錄',       sub: '付款日期、堂數、金額、累計統計' },

];

const now = new Date();
const thisYear = now.getFullYear();
const thisMonth = now.getMonth() + 1;

// 今天的日期字串（YYYY-MM-DD）
const todayStr = `${thisYear}-${String(thisMonth).padStart(2,'0')}-${String(now.getDate()).padStart(2,'0')}`;
// ★ 需求3：預設檔名用 YYYYMMDD
const todayFileName = `${thisYear}${String(thisMonth).padStart(2,'0')}${String(now.getDate()).padStart(2,'0')}`;
// 本月第一天
const monthStartStr = `${thisYear}-${String(thisMonth).padStart(2,'0')}-01`;

// 過濾事件依照時間區間（以日期為最小單位）
function filterEventsByRange(events, rangeType, customStart, customEnd) {
  return events.filter(ev => {
    if (!ev.date) return false;
    const parts = ev.date.split('/');
    const evYear = parseInt(parts[0]);
    const evMonth = parseInt(parts[1]);
    const evDay = parseInt(parts[2] || 1);
    const evDate = new Date(evYear, evMonth - 1, evDay);

    if (rangeType === 'this_month') {
      return evYear === thisYear && evMonth === thisMonth;
    }
    if (rangeType === 'last_month') {
      const lm = thisMonth === 1 ? 12 : thisMonth - 1;
      const ly = thisMonth === 1 ? thisYear - 1 : thisYear;
      return evYear === ly && evMonth === lm;
    }
    if (rangeType === 'this_quarter') {
      const qStart = Math.floor((thisMonth - 1) / 3) * 3 + 1;
      return evYear === thisYear && evMonth >= qStart && evMonth < qStart + 3;
    }
    if (rangeType === 'this_year') {
      return evYear === thisYear;
    }
    if (rangeType === 'custom' && customStart && customEnd) {
      // 直接用年月日比對，避免 UTC 時區轉換導致日期偏移
      const [sy, sm, sd] = customStart.split('-').map(Number);
      const [ey, em, ed] = customEnd.split('-').map(Number);
      const startDate = new Date(sy, sm - 1, sd);
      const endDate = new Date(ey, em - 1, ed, 23, 59, 59);
      return evDate >= startDate && evDate <= endDate;
    }
    return true;
  });
}

function getRangeLabel(rangeType, customStart, customEnd) {
  if (rangeType === 'this_month') return `${thisYear}-${String(thisMonth).padStart(2,'0')}`;
  if (rangeType === 'last_month') {
    const lm = thisMonth === 1 ? 12 : thisMonth - 1;
    const ly = thisMonth === 1 ? thisYear - 1 : thisYear;
    return `${ly}-${String(lm).padStart(2,'0')}`;
  }
  if (rangeType === 'this_quarter') return `${thisYear}Q${Math.ceil(thisMonth/3)}`;
  if (rangeType === 'this_year') return `${thisYear}`;
  if (rangeType === 'custom') return `${customStart}_${customEnd}`;
  return '';
}

export default function ExportModal({ accessToken, students, events, coachColorMap, calendarCoachMap, coaches, fetchEventsForRange, studentGroups, onClose }) {
  const [selected, setSelected] = useState({ students: true, lessons: true, payments: true });
  // 學員選取（預設全選）
  const [selectedStudentIds, setSelectedStudentIds] = useState(() => new Set(students.map(s => s.id)));
  const [studentPickerOpen, setStudentPickerOpen] = useState(false);

  function toggleStudent(id) {
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleGroup(ids) {
    const allSelected = ids.every(id => selectedStudentIds.has(id));
    setSelectedStudentIds(prev => {
      const next = new Set(prev);
      ids.forEach(id => allSelected ? next.delete(id) : next.add(id));
      return next;
    });
  }
  function selectAll() { setSelectedStudentIds(new Set(students.map(s => s.id))); }
  function selectNone() { setSelectedStudentIds(new Set()); }
  const [coachSheets, setCoachSheets] = useState({});
  const [folderId, setFolderId] = useState(null);
  const [folderName, setFolderName] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  // 時間區間 - 以日為單位
  const [rangeType, setRangeType] = useState('this_month');
  const [customStart, setCustomStart] = useState(monthStartStr);
  const [customEnd, setCustomEnd] = useState(todayStr);

  useEffect(() => {
    if (window.gapi) return;
    const script = document.createElement('script');
    script.src = 'https://apis.google.com/js/api.js';
    document.body.appendChild(script);
  }, []);

  // 暫存從 Google Calendar 抓回來的完整事件（跨月）
  const [fetchedEvents, setFetchedEvents] = useState(events);
  const [customFilename, setCustomFilename] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const fetchTimerRef = useRef(null);

  // 每次區間改變就重新抓事件
  useEffect(() => {
    clearTimeout(fetchTimerRef.current);
    fetchTimerRef.current = setTimeout(async () => {
      if (!fetchEventsForRange) return;
      setIsFetching(true);
      try {
        const start = getRangeStart(rangeType, customStart);
        const end = getRangeEnd(rangeType, customEnd);
        const rawItems = await fetchEventsForRange(start, end);
        const parsed = [];
        rawItems.forEach(ev => {
          const colorId = ev.colorId || null;
          const calendarId = ev._calendarId || null;
          const startDT = ev.start?.dateTime || ev.start?.date || '';
          const startDate = startDT ? new Date(startDT) : null;
          if (!startDate) return;
          const dateStr = `${startDate.getFullYear()}/${startDate.getMonth()+1}/${startDate.getDate()}`;
          const timeStr = ev.start?.dateTime
            ? `${String(startDate.getHours()).padStart(2,'0')}:${String(startDate.getMinutes()).padStart(2,'0')}`
            : '';
          const names = (ev.summary || '').split(/[、,，]/).map(s => s.trim()).filter(Boolean);
          names.forEach(name => {
            parsed.push({ studentName: name, colorId, calendarId, date: dateStr, time: timeStr, isTrial: false, notes: ev.description || '' });
          });
        });
        setFetchedEvents(parsed);
        console.log('[Export] fetched', parsed.length, 'events, calendarIds:', [...new Set(parsed.map(e=>e.calendarId))]);
      } catch(e) {
        console.error('fetch range failed', e);
        setFetchedEvents(events);
      } finally {
        setIsFetching(false);
      }
    }, 400);
    return () => clearTimeout(fetchTimerRef.current);
  }, [rangeType, customStart, customEnd, fetchEventsForRange, accessToken]);

  const filteredEvents = filterEventsByRange(fetchedEvents, rangeType, customStart, customEnd);

  function toggleOption(id) {
    if (id === 'students') return;
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  }

  function toggleCoachSheet(colorId) {
    setCoachSheets(prev => ({ ...prev, [colorId]: !prev[colorId] }));
  }

  function openPicker() {
    if (!window.gapi) { alert('Picker 尚未載入，請稍後再試'); return; }
    window.gapi.load('picker', () => {
      const picker = new window.google.picker.PickerBuilder()
        .addView(
          new window.google.picker.DocsView()
            .setIncludeFolders(true)
            .setSelectFolderEnabled(true)
            .setMimeTypes('application/vnd.google-apps.folder')
        )
        .setOAuthToken(accessToken)
        .setCallback(data => {
          if (data.action === window.google.picker.Action.PICKED) {
            setFolderId(data.docs[0].id);
            setFolderName(data.docs[0].name);
          }
        })
        .build();
      picker.setVisible(true);
    });
  }

  async function doExport() {
    setStatus('exporting');
    setErrorMsg('');
    try {
      const label = getRangeLabel(rangeType, customStart, customEnd);
      const title = customFilename.trim() || `${todayFileName}_課程管理系統_${label}`;
      // 依選取的學員過濾
      const exportStudents = students.filter(s => selectedStudentIds.has(s.id));

      // 決定要建立哪些基本分頁
      const baseSheets = [];
      if (selected.students) baseSheets.push('學員總覽');
      if (selected.lessons)  baseSheets.push('上課紀錄');
      if (selected.payments) baseSheets.push('付款紀錄');
      // 加上選取的教練分頁
      const coachSheetNames = coaches.filter(c => coachSheets[c.colorId]).map(c => c.name.slice(0, 31));
      const allSheetNames = [...baseSheets, ...coachSheetNames];
      if (allSheetNames.length === 0) allSheetNames.push('學員總覽');
      const spreadsheetId = await createSpreadsheet(accessToken, title, allSheetNames, folderId || null);

      // filteredEvents 已是正確區間的完整資料（由 useEffect 預先抓取）
      let exportEvents = filteredEvents.filter(ev => exportStudents.some(s => s.name === ev.studentName));

      if (selected.students) await writeSheet(accessToken, spreadsheetId, '學員總覽', formatStudentsSheet(exportStudents, coachColorMap, calendarCoachMap));
      if (selected.lessons)  await writeSheet(accessToken, spreadsheetId, '上課紀錄', formatLessonsSheet(exportEvents, coachColorMap, calendarCoachMap));
      if (selected.payments) await writeSheet(accessToken, spreadsheetId, '付款紀錄', formatPaymentsSheet(exportStudents));
      for (const c of coaches) {
        if (!coachSheets[c.colorId]) continue;
        const coachEvents = exportEvents.filter(ev =>
          (c.calendarId && ev.calendarId === c.calendarId) ||
          (ev.colorId && ev.colorId === c.colorId)
        );
        if (coachEvents.length === 0) continue;
        await writeSheet(accessToken, spreadsheetId, c.name.slice(0, 31), formatLessonsSheet(coachEvents, coachColorMap, calendarCoachMap));
      }

      setStatus('done');
    } catch (e) {
      setStatus('error');
      setErrorMsg(e.message);
    }
  }

  const selectedSheets = [];
  if (selected.students) selectedSheets.push({ label: '學員總覽',  bg: '#e6f4ea', color: '#137333' });
  if (selected.lessons)  selectedSheets.push({ label: '上課紀錄',  bg: '#e8f0fe', color: '#1967d2' });
  if (selected.payments) selectedSheets.push({ label: '付款紀錄',  bg: '#fef9e7', color: '#b46309' });
  coaches.forEach(c => {
    if (coachSheets[c.colorId]) selectedSheets.push({ label: c.name, bg: c.hex + '33', color: c.hex });
  });

  const rangeOptions = [
    { value: 'this_month',   label: '本月' },
    { value: 'last_month',   label: '上個月' },
    { value: 'this_quarter', label: '本季' },
    { value: 'this_year',    label: '今年全年' },
    { value: 'custom',       label: '自訂區間' },
  ];

  return (
    <div style={S.overlay}>
      <div style={S.modal}>
        <div style={S.mhead}>
          <span style={{ fontSize: 15, fontWeight: 500 }}>匯出設定</span>
          <button style={S.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ padding: '18px 20px', maxHeight: 520, overflowY: 'auto' }}>

          {/* 時間區間 */}
          <div style={{ marginBottom: 16 }}>
            <div style={S.sectionLbl}>時間區間</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
              {rangeOptions.map(opt => (
                <button
                  key={opt.value}
                  style={{ ...S.rangeBtn, ...(rangeType === opt.value ? S.rangeBtnActive : {}) }}
                  onClick={() => setRangeType(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            {rangeType === 'custom' && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 14px', background: '#f8f8f8', borderRadius: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>開始日期</div>
                  <input
                    type="date"
                    style={S.dateInput}
                    value={customStart}
                    max={customEnd}
                    onChange={e => setCustomStart(e.target.value)}
                  />
                </div>
                <div style={{ fontSize: 13, color: '#aaa', paddingTop: 16 }}>→</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#888', marginBottom: 4 }}>結束日期</div>
                  <input
                    type="date"
                    style={S.dateInput}
                    value={customEnd}
                    min={customStart}
                    max={todayStr}
                    onChange={e => setCustomEnd(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div style={{ marginTop: 8, fontSize: 12, color: '#888', display: 'flex', alignItems: 'center', gap: 6 }}>
              {isFetching
                ? <span style={{ color: '#aaa' }}>載入中...</span>
                : <>此區間共 <span style={{ fontWeight: 500, color: '#222', margin: '0 2px' }}>{filteredEvents.length}</span> 堂課程紀錄</>
              }
            </div>
          </div>

          {/* 檔案名稱 */}
          <div style={{ marginBottom: 16 }}>
            <div style={S.sectionLbl}>檔案名稱</div>
            <input
              style={{ width: '100%', border: '0.5px solid #ccc', borderRadius: 8, padding: '8px 10px', fontSize: 13, boxSizing: 'border-box' }}
              placeholder={`${todayFileName}_課程管理系統_${getRangeLabel(rangeType, customStart, customEnd)}（留空使用預設）`}
              value={customFilename}
              onChange={e => setCustomFilename(e.target.value)}
            />
          </div>

          {/* 儲存位置 */}
          <div style={{ marginBottom: 16 }}>
            <div style={S.sectionLbl}>儲存位置（Google 雲端硬碟）</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button style={S.folderBtn} onClick={openPicker}>選擇資料夾</button>
              {folderName
                ? <span style={{ fontSize: 13, color: '#188038' }}>📁 {folderName}</span>
                : <span style={{ fontSize: 12, color: '#999' }}>未選取，存到 Drive 根目錄</span>
              }
            </div>
          </div>

          {/* 學員選取 */}
          <div style={{ ...S.sectionLbl, marginBottom: 6 }}>選取學員</div>
          <div style={{ border: '0.5px solid #e0e0e0', borderRadius: 8, overflow: 'hidden', marginBottom: 14 }}>
            {/* 標題列 */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#f8f8f8', borderBottom: '0.5px solid #eee' }}>
              <span style={{ fontSize: 12, color: '#555' }}>
                已選 {selectedStudentIds.size} / {students.length} 人
              </span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ fontSize: 11, color: '#1967d2', background: 'none', border: 'none', cursor: 'pointer' }} onClick={selectAll}>全選</button>
                <button style={{ fontSize: 11, color: '#888', background: 'none', border: 'none', cursor: 'pointer' }} onClick={selectNone}>清除</button>
                <button style={{ fontSize: 11, color: '#555', background: 'none', border: 'none', cursor: 'pointer' }} onClick={() => setStudentPickerOpen(v => !v)}>
                  {studentPickerOpen ? '▲ 收合' : '▼ 展開選取'}
                </button>
              </div>
            </div>

            {studentPickerOpen && (
              <div style={{ maxHeight: 260, overflowY: 'auto', padding: '8px 12px' }}>
                {(() => {
                  const groups = studentGroups || [];
                  const groupedIds = new Set(groups.flatMap(g => g.studentIds || []));
                  const ungrouped = students.filter(s => !groupedIds.has(s.id));

                  return (
                    <>
                      {groups.map(group => {
                        const groupStudents = students.filter(s => (group.studentIds || []).includes(s.id));
                        if (groupStudents.length === 0) return null;
                        const allOn = groupStudents.every(s => selectedStudentIds.has(s.id));
                        const someOn = groupStudents.some(s => selectedStudentIds.has(s.id));
                        return (
                          <div key={group.id} style={{ marginBottom: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                              <div
                                style={{ width: 14, height: 14, borderRadius: 3, border: `0.5px solid ${allOn ? '#188038' : someOn ? '#188038' : '#ccc'}`, background: allOn ? '#188038' : someOn ? '#c8e6c9' : '#fff', cursor: 'pointer', flexShrink: 0 }}
                                onClick={() => toggleGroup(groupStudents.map(s => s.id))}
                              />
                              <span style={{ fontSize: 12, fontWeight: 500, color: '#444' }}>{group.name}</span>
                              <span style={{ fontSize: 11, color: '#aaa' }}>{groupStudents.filter(s => selectedStudentIds.has(s.id)).length}/{groupStudents.length}</span>
                            </div>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, paddingLeft: 22 }}>
                              {groupStudents.map(s => (
                                <div key={s.id}
                                  style={{ padding: '3px 8px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: `0.5px solid ${selectedStudentIds.has(s.id) ? '#188038' : '#ddd'}`, background: selectedStudentIds.has(s.id) ? '#e6f4ea' : '#fff', color: selectedStudentIds.has(s.id) ? '#188038' : '#666' }}
                                  onClick={() => toggleStudent(s.id)}
                                >
                                  {s.name}
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                      {ungrouped.length > 0 && (
                        <div style={{ marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                            <div
                              style={{ width: 14, height: 14, borderRadius: 3, border: `0.5px solid ${ungrouped.every(s => selectedStudentIds.has(s.id)) ? '#188038' : '#ccc'}`, background: ungrouped.every(s => selectedStudentIds.has(s.id)) ? '#188038' : '#fff', cursor: 'pointer', flexShrink: 0 }}
                              onClick={() => toggleGroup(ungrouped.map(s => s.id))}
                            />
                            <span style={{ fontSize: 12, fontWeight: 500, color: '#444' }}>未分類</span>
                            <span style={{ fontSize: 11, color: '#aaa' }}>{ungrouped.filter(s => selectedStudentIds.has(s.id)).length}/{ungrouped.length}</span>
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, paddingLeft: 22 }}>
                            {ungrouped.map(s => (
                              <div key={s.id}
                                style={{ padding: '3px 8px', borderRadius: 20, fontSize: 11, cursor: 'pointer', border: `0.5px solid ${selectedStudentIds.has(s.id) ? '#188038' : '#ddd'}`, background: selectedStudentIds.has(s.id) ? '#e6f4ea' : '#fff', color: selectedStudentIds.has(s.id) ? '#188038' : '#666' }}
                                onClick={() => toggleStudent(s.id)}
                              >
                                {s.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* 匯出內容 */}
          <div style={S.sectionLbl}>匯出內容</div>
          {EXPORT_OPTIONS.map(opt => (
            <div key={opt.id} style={{ ...S.optRow, ...(selected[opt.id] ? S.optRowOn : {}) }} onClick={() => toggleOption(opt.id)}>
              <div style={{ ...S.checkbox, ...(selected[opt.id] ? S.checkboxOn : {}) }}>
                {selected[opt.id] && <div style={S.checkmark} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#222', display: 'flex', alignItems: 'center', gap: 6 }}>
                  {opt.label}
                  {opt.required && <span style={S.tagGreen}>必選</span>}
                  {opt.tag && <span style={S.tagPurple}>{opt.tag}</span>}
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>{opt.sub}</div>
              </div>
            </div>
          ))}

          {/* 依教練分頁 */}
          {coaches.length > 0 && (
            <>
              <div style={{ ...S.sectionLbl, marginTop: 14 }}>依教練個別匯出（各自獨立分頁）</div>
              {coaches.map(c => (
                <div key={c.colorId} style={{ ...S.optRow, ...(coachSheets[c.colorId] ? S.optRowOn : {}) }} onClick={() => toggleCoachSheet(c.colorId)}>
                  <div style={{ ...S.checkbox, ...(coachSheets[c.colorId] ? S.checkboxOn : {}) }}>
                    {coachSheets[c.colorId] && <div style={S.checkmark} />}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.hex, flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#222' }}>{c.name} 的課程</div>
                      <div style={{ fontSize: 11, color: '#888' }}>
                        此區間共 {filteredEvents.filter(ev => (c.calendarId && ev.calendarId === c.calendarId) || ev.colorId === c.colorId).length} 堂，獨立分頁
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </>
          )}

          {/* 預計分頁 */}
          <div style={{ marginTop: 14, background: '#f8f8f8', borderRadius: 8, padding: '10px 12px' }}>
            <div style={{ fontSize: 11, color: '#888', marginBottom: 6 }}>
              檔名：<span style={{ fontWeight: 500, color: '#444' }}>{todayFileName}_課程管理系統_{getRangeLabel(rangeType, customStart, customEnd)}</span>
              　共 {selectedSheets.length} 個分頁
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {selectedSheets.map((s, i) => (
                <span key={i} style={{ background: s.bg, color: s.color, fontSize: 12, padding: '3px 10px', borderRadius: 20, fontWeight: 500 }}>{s.label}</span>
              ))}
            </div>
          </div>

          {status === 'done' && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#e6f4ea', borderRadius: 8, fontSize: 13, color: '#137333' }}>
              匯出成功！請至 Google 雲端硬碟查看。
            </div>
          )}
          {status === 'error' && (
            <div style={{ marginTop: 12, padding: '10px 12px', background: '#fce8e6', borderRadius: 8, fontSize: 13, color: '#c5221f' }}>
              匯出失敗：{errorMsg}
            </div>
          )}
        </div>

        <div style={S.mfoot}>
          <button style={S.btnS} onClick={onClose}>關閉</button>
          {status !== 'done' && (
            <button style={S.btnP} onClick={doExport} disabled={status === 'exporting'}>
              {status === 'exporting' ? '匯出中...' : '匯出至 Google Sheets'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, border: '0.5px solid #e0e0e0', width: 520, maxWidth: '95vw' },
  mhead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '0.5px solid #eee' },
  mfoot: { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '0.5px solid #eee' },
  closeBtn: { background: 'none', border: 'none', fontSize: 20, color: '#888', lineHeight: 1, cursor: 'pointer' },
  sectionLbl: { fontSize: 11, fontWeight: 500, color: '#999', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 8 },
  rangeBtn: { padding: '6px 14px', borderRadius: 20, fontSize: 13, background: 'none', border: '0.5px solid #ddd', color: '#666', cursor: 'pointer' },
  rangeBtnActive: { background: '#222', borderColor: '#222', color: '#fff', fontWeight: 500 },
  dateInput: { width: '100%', border: '0.5px solid #ccc', borderRadius: 8, padding: '7px 10px', fontSize: 13, background: '#fff' },
  folderBtn: { padding: '7px 14px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#444', cursor: 'pointer' },
  optRow: { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', borderRadius: 8, cursor: 'pointer', border: '0.5px solid #eee', marginBottom: 6 },
  optRowOn: { borderColor: '#188038', background: '#f0faf3' },
  checkbox: { width: 16, height: 16, border: '0.5px solid #ccc', borderRadius: 3, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1, background: '#fff' },
  checkboxOn: { background: '#188038', borderColor: '#188038' },
  checkmark: { width: 9, height: 6, borderLeft: '2px solid #fff', borderBottom: '2px solid #fff', transform: 'rotate(-45deg) translate(1px,-1px)' },
  tagGreen: { fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#e6f4ea', color: '#137333' },
  tagPurple: { fontSize: 10, padding: '1px 6px', borderRadius: 20, background: '#f3e8fd', color: '#7b1fa2' },
  btnS: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: 'none', border: '0.5px solid #ccc', color: '#666', cursor: 'pointer' },
  btnP: { padding: '7px 16px', borderRadius: 8, fontSize: 13, background: '#188038', border: 'none', color: '#fff', fontWeight: 500, cursor: 'pointer' },
};
